/**
 * Runtime Configuration Loader
 *
 * Supports two configuration sources with automatic detection:
 * 1. Build-time variables (.env file) - Any variable in .env is automatically build-time only
 * 2. Runtime variables (config.json) - Variables not in .env are read from config.json
 *
 * Rules (automatic):
 * - If variable exists in .env: Use .env value (build-time, never from config.json)
 * - If variable doesn't exist in .env: Use config.json value (runtime, never from .env)
 *
 * Config loading priority:
 * 1. First: Check for embedded config in HTML (window.__RUNTIME_CONFIG__) - no network request
 * 2. Fallback: Fetch from /config.json - only if not embedded
 */

const CONFIG_URL = '/config.json';

let runtimeConfig: Record<string, unknown> | null = null;
let configLoadPromise: Promise<Record<string, unknown>> | null = null;
let configLoadError: Error | null = null;

function deepClone<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') return obj;
    return JSON.parse(JSON.stringify(obj)) as T;
}

function deepFreeze<T>(obj: T): T {
    if (obj === null || typeof obj !== 'object') return obj;
    const o = obj as Record<string, unknown>;
    Object.freeze(o);
    Object.keys(o).forEach((key) => deepFreeze(o[key]));
    return obj;
}

function cloneAndFreezeConfig(
    config: Record<string, unknown>
): Record<string, unknown> {
    return deepFreeze(deepClone(config));
}

const FROZEN_EMPTY_CONFIG = Object.freeze({}) as Record<string, unknown>;

function getEmbeddedConfig(): Record<string, unknown> | null {
    if (typeof window === 'undefined') return null;

    const w = window as unknown as { __RUNTIME_CONFIG__?: unknown };
    if (w.__RUNTIME_CONFIG__) {
        try {
            let config: Record<string, unknown>;
            if (
                typeof w.__RUNTIME_CONFIG__ === 'object' &&
                w.__RUNTIME_CONFIG__ !== null
            ) {
                config = w.__RUNTIME_CONFIG__ as Record<string, unknown>;
            } else if (typeof w.__RUNTIME_CONFIG__ === 'string') {
                config = JSON.parse(w.__RUNTIME_CONFIG__) as Record<
                    string,
                    unknown
                >;
            } else {
                return null;
            }
            return cloneAndFreezeConfig(config);
        } catch {
            return null;
        }
    }

    const rootElement = document.getElementById('root');
    const data = rootElement?.getAttribute('data-runtime-config');
    if (data) {
        try {
            const config = JSON.parse(data) as Record<string, unknown>;
            return cloneAndFreezeConfig(config);
        } catch {
            return null;
        }
    }

    return null;
}

function getBaseUrl(): string {
    if (typeof window !== 'undefined') return window.location.origin;
    return '';
}

/**
 * Loads the runtime configuration from embedded HTML or /config.json.
 */
export function loadRuntimeConfig(): Promise<Record<string, unknown>> {
    if (configLoadPromise) return configLoadPromise;
    if (runtimeConfig !== null) return Promise.resolve(runtimeConfig);

    configLoadPromise = (async (): Promise<Record<string, unknown>> => {
        try {
            const embeddedConfig = getEmbeddedConfig();
            if (embeddedConfig) {
                runtimeConfig = embeddedConfig;
                configLoadError = null;
                return runtimeConfig;
            }

            const baseUrl = getBaseUrl();
            const configUrl = `${baseUrl}${CONFIG_URL}`;

            const response = await fetch(configUrl, {
                method: 'GET',
                headers: { Accept: 'application/json' },
                cache: 'no-cache',
                credentials: 'same-origin',
            });

            if (!response.ok) {
                if (response.status === 404) {
                    configLoadError = new Error('Config file not found (404)');
                    runtimeConfig = FROZEN_EMPTY_CONFIG;
                    return runtimeConfig;
                }
                configLoadError = new Error(
                    `Failed to load config: ${response.status}`
                );
                runtimeConfig = FROZEN_EMPTY_CONFIG;
                return runtimeConfig;
            }

            const text = await response.text();
            if (!text?.trim()) {
                configLoadError = new Error('Config file is empty');
                runtimeConfig = FROZEN_EMPTY_CONFIG;
                return runtimeConfig;
            }

            const contentType = response.headers.get('content-type');
            if (contentType && !contentType.includes('application/json')) {
                if (
                    text.trim().startsWith('<!DOCTYPE') ||
                    text.trim().startsWith('<html')
                ) {
                    configLoadError = new Error(
                        'Server returned HTML instead of JSON'
                    );
                    runtimeConfig = FROZEN_EMPTY_CONFIG;
                    return runtimeConfig;
                }
                if (text.includes('=') && !text.trim().startsWith('{')) {
                    configLoadError = new Error(
                        'Config file is in env format, not JSON'
                    );
                    runtimeConfig = FROZEN_EMPTY_CONFIG;
                    return runtimeConfig;
                }
            }

            let config: Record<string, unknown>;
            try {
                config = JSON.parse(text) as Record<string, unknown>;
            } catch {
                configLoadError = new Error('Invalid JSON in config file');
                runtimeConfig = FROZEN_EMPTY_CONFIG;
                return runtimeConfig;
            }

            runtimeConfig = cloneAndFreezeConfig(
                config && typeof config === 'object' ? config : {}
            );
            configLoadError = null;
            return runtimeConfig;
        } catch (error) {
            configLoadError =
                error instanceof Error ? error : new Error(String(error));
            runtimeConfig = FROZEN_EMPTY_CONFIG;
            return runtimeConfig;
        }
    })();

    return configLoadPromise;
}

/**
 * Returns a deep clone of the loaded runtime config so callers cannot mutate the stored config.
 */
export function getRuntimeConfig(): Record<string, unknown> | null {
    if (runtimeConfig === null) return null;
    return deepClone(runtimeConfig);
}

/**
 * Gets a configuration value with strict separation between build-time (.env) and runtime (config.json).
 *
 * 1. If variable exists in .env (process.env): use .env value (build-time, never from config.json)
 * 2. If variable doesn't exist in .env: use config.json value (runtime, never from .env)
 * 3. Otherwise return defaultValue
 */
export function getConfigValue(
    key: string,
    defaultValue?: string
): string | undefined {
    if (typeof process !== 'undefined' && process.env?.[key] !== undefined) {
        return process.env[key];
    }
    if (runtimeConfig && runtimeConfig[key] !== undefined) {
        const v = runtimeConfig[key];
        return typeof v === 'string' ? v : String(v);
    }
    return defaultValue;
}

export function isRuntimeConfigLoaded(): boolean {
    return runtimeConfig !== null;
}

export function getConfigLoadError(): Error | null {
    return configLoadError;
}
